<template>
	<div
		v-if="isMounted && advert && !error"
		class="flex justify-center items-center"
	>
		<div ref="clickRef" class="relative">
			<Transition name="fade">
				<span
					v-if="!isOutside && advert.assetTitle?.length"
					class="absolute -translate-x-1/2 -translate-y-full mb-4 max-w-[15rem] inline-block left-[var(--x)] top-[var(--y)] bg-background-secondary rounded-sm typo-body-caption px-2 py-1 whitespace-nowrap"
					:style="bubbleElementPosition"
				>
					{{ advert.assetTitle }}
				</span>
			</Transition>

			<component
				:is="imgUrl ? NuxtLink : 'div'"
				:to="imgUrl"
				class="block"
			>
				<NuxtImg
					:src="imgAsset"
					:alt="advert.assetTitle ?? ''"
					:class="imageClasses"
					v-bind="imgAttributes"
					provider="storyblok"
				/>
			</component>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {useElementVisibility, useMouseInElement} from '@vueuse/core';
	import {storeToRefs} from 'pinia';
	import {findKey} from '@dovca/toolbox';
	import {useClickStore} from '@/stores/click';
	import {useStoryblokHypermarketsStore} from '@/stores/storyblokHypermarkets';
	import {type Ad, AdPosition, AdsService, AdUserStateTarget} from '@/openapi';
	import {useCustomerProfileStore} from '@/stores/customerProfile';
	import type {screens} from '@/config/tailwind';

	type AdSize = {
		width: number;
		height: number;
		asset: string;
	};

	type Props = {
		placement: AdPosition;
		customAsset?: AdSize;
	};

	type BreakpointName = keyof typeof screens;
	type BreakpointCategory = 'mobile' | 'mobileSmall' | 'tablet' | 'tabletSmall' | 'desktop' | 'desktopSmall' | 'desktopLarge';
	type BreakpointCategoryMap = Record<BreakpointCategory, BreakpointName[]>;

	const props = defineProps<Props>();

	const {customerSessionId} = storeToRefs(useClickStore());
	const {selectedHypermarketInstance} = storeToRefs(useStoryblokHypermarketsStore());
	const {profile, userIsLogged} = storeToRefs(useCustomerProfileStore());
	const isMounted = useMounted();

	const NuxtLink = resolveComponent('NuxtLink');

	const clickRef = ref(null);
	const targetIsVisible = useElementVisibility(clickRef);
	const {isMobile} = useDevice();

	const {elementY, elementX, isOutside} = useMouseInElement(clickRef);
	const bubbleElementPosition = computed(() => ({
		top: `${elementY.value - 8}px`,
		left: `${elementX.value}px`,
	}));

	const currentBreakpoint = useCurrentBreakpoint();

	const breakpointCategoryList: BreakpointCategory[] = [
		'mobileSmall',
		'mobile',
		'tabletSmall',
		'tablet',
		'desktopSmall',
		'desktop',
		'desktopLarge',
	];

	const breakpointCategories: BreakpointCategoryMap = {
		mobileSmall: ['default'], // < 460
		mobile: ['xs'], // 460–599
		tabletSmall: ['sm'], // 600–899
		tablet: ['md'], // 900–1199
		desktopSmall: ['lg'], // 1200–1439
		desktop: ['xl'], // 1440–1919
		desktopLarge: ['xxl'], // 1920+
	};

	const fallbackCategoryMap: Record<BreakpointCategory, 'desktop' | 'tablet' | 'mobile'> = {
		desktop: 'desktop',
		desktopSmall: 'desktop',
		desktopLarge: 'desktop',
		tablet: 'tablet',
		tabletSmall: 'tablet',
		mobile: 'mobile',
		mobileSmall: 'mobile',
	};

	const isBreakpoint = (category: BreakpointCategory) => computed(() => currentBreakpoint.value && breakpointCategories[category].includes(currentBreakpoint.value));
	const isMobileBreakpoint = computed(() => isBreakpoint('mobile').value || isBreakpoint('mobileSmall').value);

	const imgAttributes = computed(() => getImageSize(props.placement, currentBreakpoint.value).attributes);
	const imgAsset = computed(() => {
		const assetType = getImageSize(props.placement, currentBreakpoint.value).asset;
		switch (assetType) {
			case 'mobile':
				return advert.value?.assetMobile || '';
			case 'tablet':
				return advert.value?.assetTablet || '';
			case 'desktop':
				return advert.value?.assetDesktop || '';
			default:
				return advert.value?.assetMobile || '';
		};
	});
	const imgUrl = computed(() => {
		if (isMobileBreakpoint.value) {
			return advert.value?.urlMobile ?? null;
		}
		return advert.value?.url ?? null;
	});

	const targetValues = computed<AdUserStateTarget[]>(() => {
		const targets = [userIsLogged.value ? AdUserStateTarget.LOGGED : AdUserStateTarget.NOT_LOGGED];
		if (profile.value?.contacts?.emailSendNewsletterFlag) {
			targets.push(AdUserStateTarget.ATLEAST_ONE_NEWSLETTER);
		}
		return [targets.join(',') as unknown as AdUserStateTarget];
	});

	const {data: advert, error} = await useLazyAsyncData<Ad>(`click-${props.placement}`, () => AdsService.getAdForSpace({
		placement: props.placement,
		sessionId: customerSessionId.value!,
		target: targetValues.value,
		customerId: profile.value?.registration?.customerID,
		houseNumber: Number(selectedHypermarketInstance.value?.content?.gsoaId) || undefined,
	}), {watch: [selectedHypermarketInstance, userIsLogged], server: false});

	const defaultSizes: {
		desktop: AdSize;
		tablet: AdSize;
		mobile: AdSize;
		custom: Partial<Record<AdPosition, Partial<Record<BreakpointCategory, AdSize>>>>;
	} = {
		desktop: {width: 975, height: 250, asset: 'desktop'},
		tablet: {width: 480, height: 320, asset: 'tablet'},
		mobile: {width: 300, height: 300, asset: 'mobile'},
		custom: {
			[AdPosition.ACTION_OFFER_FIRST]: {
				desktop: {width: 480, height: 320, asset: 'desktop'},
				tablet: {width: 480, height: 320, asset: 'tablet'},
				mobile: {width: 300, height: 300, asset: 'mobile'},
			},
			[AdPosition.ACTION_OFFER_SECOND]: {
				desktop: {width: 480, height: 320, asset: 'desktop'},
				tablet: {width: 480, height: 320, asset: 'tablet'},
				mobile: {width: 300, height: 300, asset: 'mobile'},
			},
			[AdPosition.ACTION_OFFER_THIRD]: {
				desktop: {width: 480, height: 320, asset: 'desktop'},
				tablet: {width: 480, height: 320, asset: 'tablet'},
				mobile: {width: 300, height: 300, asset: 'mobile'},
			},
			[AdPosition.ACTION_OFFER_FOURTH]: {
				desktop: {width: 480, height: 320, asset: 'desktop'},
				tablet: {width: 480, height: 320, asset: 'tablet'},
				mobile: {width: 300, height: 300, asset: 'mobile'},
			},
			[AdPosition.ACTION_OFFER_LEAFLET_FIRST]: {
				desktop: {width: 300, height: 300, asset: 'desktop'},
				desktopSmall: {width: 300, height: 300, asset: 'desktop'},
				desktopLarge: {width: 300, height: 300, asset: 'desktop'},
				tablet: {width: 300, height: 300, asset: 'desktop'},
				tabletSmall: {width: 300, height: 300, asset: 'desktop'},
				mobile: {width: 450, height: 130, asset: 'tablet'},
				mobileSmall: {width: 300, height: 300, asset: 'desktop'},
			},
			[AdPosition.ACTION_OFFER_LEAFLET_SECOND]: {
				desktop: {width: 300, height: 300, asset: 'desktop'},
				desktopSmall: {width: 300, height: 300, asset: 'desktop'},
				desktopLarge: {width: 300, height: 300, asset: 'desktop'},
				tablet: {width: 820, height: 200, asset: 'tablet'},
				tabletSmall: {width: 450, height: 130, asset: 'mobile'},
				mobile: {width: 450, height: 130, asset: 'mobile'},
				mobileSmall: {width: 300, height: 300, asset: 'desktop'},
			},
		},
	};

	const imageClasses = computed(() => ({'rounded-[1.25rem] overflow-hidden': !!advert.value?.roundedCorners}));

	const sizes = computed(() =>
		Object.values(AdPosition).reduce(
			(acc, position) => {
				breakpointCategoryList.forEach((category) => {
					const fallback = fallbackCategoryMap[category];
					const customCategory = defaultSizes.custom[position]?.[category];
					const customFallback = defaultSizes.custom[position]?.[fallback];

					acc[category][position] = customCategory && customCategory.width
						? customCategory
						: customFallback && customFallback.width
							? customFallback
							: defaultSizes[fallback];
				});
				return acc;
			},
			breakpointCategoryList.reduce(
				(obj, category) => ({...obj, [category]: {}}),
				{} as Record<BreakpointCategory, Record<AdPosition, AdSize>>,
			),
		),
	);

	watch(targetIsVisible, async (value) => {
		const storyblokUuid = advert.value?.storyblokUuid;
		if (!value || !storyblokUuid) {
			return;
		}

		await AdsService.postSpaceTrackview({
			requestBody: {
				storyblokUuid,
				placement: props.placement,
				customerId: profile.value?.registration?.customerID,
				isMobile,
			},
		});
	});

	function getImageSize(placement: AdPosition, breakpoint?: BreakpointName) {
		if (props.customAsset) {
			return {
				attributes: {
					width: props.customAsset.width,
					height: props.customAsset.height,
				},
				asset: props.customAsset.asset,
			};
		}

		if (!breakpoint) {
			return {width: 0, height: 0};
		}

		const category = findKey<BreakpointCategoryMap>((v) => v.includes(breakpoint))(
			breakpointCategories,
		);

		if (!category) {
			return {attributes: {width: 0, height: 0}, asset: 'mobile'};
		}

		const size = sizes.value[category][placement];

		return {
			attributes: {
				width: size.width,
				height: size.height,
			},
			asset: size.asset,
		};
	}
</script>
